import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthToken } from '../../utils/cookieUtils';
import { isDockUnavailableForMovement } from '../../utils/dock';
import {
  Paper,
  Typography,
  Chip,
  IconButton,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Stack,
  Divider
} from '@mui/material';
import {
  Refresh,
  Visibility,
  Cancel,
  CheckCircle,
  DirectionsCar,
  Add,
  Remove,
  ExpandMore,
  FilterList,
  Clear,
  AssignmentReturn
} from '@mui/icons-material';
import {
  saveFacilityToStorage,
  loadFacilityFromStorage,
  formatFacilityOptions
} from '../../utils/facilityStorage';
import { jwtDecode } from 'jwt-decode';
import TaskHistoryExportModal from '../../components/TaskHistoryExportModal';
import { REPORTS_WEBSOCKET_URL } from '../../constants';

// GraphQL API URL
const GRAPHQL_API_URL = process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql';

// Interface for facility
interface Facility {
  id: number;
  name: string;
}

// Types for tasks
interface TaskTemplate {
  uuid: string;
  name: string;
  task_template_json: any;
  account_id?: string;
}

interface TaskType {
  uuid: string;
  type: string;
  class: string;
}

interface TaskStatus {
  uuid: string;
  status: string;
}

// Union types for location objects
interface DockObject {
  uuid: string;
  name: string;
  status?: string;
}

interface YardSpotObject {
  uuid: string;
  name: string;
  facilityZoneUuid?: string;
}

interface TrailerObject {
  uuid: string;
  number: string;
}

interface ExternalLocationObject {
  name: string;
}

interface FacilityZoneObject {
  uuid: string;
  name: string;
}

interface Trailer {
  trailer_number: string;
  carrier?: {
    name: string;
  };
}

interface Task {
  uuid: string;
  data: any;
  due_at: string | null;
  completed_at: string | null;
  user_id: string | null;
  facility_id: string;
  driver_id: string | null;
  completed_by_user_id: string | null;
  completed_by_driver_id: string | null;
  modified_by_user_id: string | null;
  metadata: any;
  created_at: string;
  updated_at: string;
  task_template: TaskTemplate | null;
  task_type: TaskType | null;
  task_status: TaskStatus | null;
  from_location: string | null;
  to_location: string | null;
  from_object: DockObject | YardSpotObject | TrailerObject | ExternalLocationObject | FacilityZoneObject | null;
  to_object: DockObject | YardSpotObject | TrailerObject | ExternalLocationObject | FacilityZoneObject | null;
  trailer: Trailer | null;
  driver: {
    firstName: string;
    lastName: string;
  } | null;
  modified_by_user: {
    username: string;
  } | null;
  related_movement_task: {
    uuid: string;
    from_location: string | null;
    to_location: string | null;
    from_object: DockObject | YardSpotObject | TrailerObject | ExternalLocationObject | FacilityZoneObject | null;
    to_object: DockObject | YardSpotObject | TrailerObject | ExternalLocationObject | FacilityZoneObject | null;
    data: {
      movement_type: string;
    };
  } | null;
}

// Interface for trailer
interface TrailerInFacility {
  uuid: string;
  trailer_number: string;
  carrier?: {
    name: string;
  };
  current_location?: {
    __typename?: string;
    uuid?: string;
    name: string;
  };
}

// Interface for dock doors
interface DockDoor {
  dockUuid: string;
  dockName: string;
  zoneUuid: string;
  dockStatus?: string;
  trailer_name?: string;
  carrier_name?: string;
}

// Interface for yard spot
interface YardSpot {
  yard_spot_uuid: string;
  yard_spot_name: string;
  facility_zone_uuid: string;
  facility_zone_name: string;
  yard_spot_status?: string;
  trailer_name?: string;
  carrier_name?: string;
}



const Tasks = () => {
  const navigate = useNavigate();
  
  // State for facilities
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [selectedFacility, setSelectedFacilityState] = useState<Facility | null>(() => {
    // Initialize from localStorage on mount
    return loadFacilityFromStorage();
  });

  // Wrapper function to save facility selection to localStorage
  const setSelectedFacility = (facility: Facility | null) => {
    setSelectedFacilityState(facility);
    saveFacilityToStorage(facility);
  };
  
  // State
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [processingTasks, setProcessingTasks] = useState<Set<string>>(new Set());
  const [showArchived, setShowArchived] = useState<boolean>(false);
  const [isTogglingArchived, setIsTogglingArchived] = useState<boolean>(false);

  // Checklist completion state - maps task UUID to array of boolean values
  const [checklistCompletionState, setChecklistCompletionState] = useState<Record<string, boolean[]>>({});

  // Task history export modal state
  const [showTaskHistoryExportModal, setShowTaskHistoryExportModal] = useState<boolean>(false);

  // Create task modal states
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState<boolean>(false);
  const [selectedTaskType, setSelectedTaskType] = useState<string>('');
  const [trailersInFacility, setTrailersInFacility] = useState<TrailerInFacility[]>([]);
  const [selectedTrailer, setSelectedTrailer] = useState<string>('');
  const [isLoadingTrailers, setIsLoadingTrailers] = useState<boolean>(false);
  const [isCreatingTask, setIsCreatingTask] = useState<boolean>(false);

  // Location selection states for MOVE_TRAILER
  const [selectedToLocationType, setSelectedToLocationType] = useState<string>('');
  const [selectedToLocation, setSelectedToLocation] = useState<string>('');
  const [availableDockDoors, setAvailableDockDoors] = useState<DockDoor[]>([]);
  const [availableYardSpots, setAvailableYardSpots] = useState<YardSpot[]>([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState<boolean>(false);
  const [autoComplete, setAutoComplete] = useState<boolean>(false);

  // Get default date (today)
  const getDefaultDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // YYYY-MM-DD format
  };

  // Filter states
  const [fromLocationFilter, setFromLocationFilter] = useState<string>('');
  const [toLocationFilter, setToLocationFilter] = useState<string>('');
  const [locationTypeFilter, setLocationTypeFilter] = useState<string>('');
  const [completedByFilter, setCompletedByFilter] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>(() => getDefaultDate());
  const [taskTypeFilter, setTaskTypeFilter] = useState<string>('');
  const [taskStatusFilter, setTaskStatusFilter] = useState<string>('');
  const [movementTypeFilter, setMovementTypeFilter] = useState<string>('');
  const [trailerNumberFilter, setTrailerNumberFilter] = useState<string>('');
  const [scacFilter, setScacFilter] = useState<string>('');

  // Check if task is a workflow with checklist
  const isWorkflowWithChecklist = (task: Task) => {
    return task.task_type?.type === 'WORKFLOW' && 
           task.data && 
           task.data.checklist && 
           Array.isArray(task.data.checklist) && 
           task.data.checklist.length > 0;
  };

  // Format workflow type (replace _ with space and title case)
  const formatWorkflowType = (workflowType: string) => {
    return workflowType
      .replace(/_/g, ' ')
      .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase());
  };

  // Initialize checklist completion state for a task
  const initializeChecklistState = (taskUuid: string, checklistLength: number) => {
    if (!checklistCompletionState[taskUuid]) {
      setChecklistCompletionState(prev => ({
        ...prev,
        [taskUuid]: new Array(checklistLength).fill(false)
      }));
    }
  };

  // Check if all checklist items are completed for a task
  const isChecklistCompleted = (taskUuid: string) => {
    const completionState = checklistCompletionState[taskUuid];
    if (!completionState) return false;
    return completionState.every(item => item === true);
  };

  // Check if a workflow task can be completed
  const canCompleteWorkflowTask = (task: Task) => {
    if (!isWorkflowWithChecklist(task)) return true; // Non-checklist workflows can be completed normally
    return isChecklistCompleted(task.uuid);
  };



  // Get location type from task object
  const getLocationType = (locationObject: any): string => {
    if (!locationObject) return 'unknown';
    if ('dockUuid' in locationObject || locationObject.__typename === 'DockObject') return 'dock';
    if ('yard_spot_uuid' in locationObject || locationObject.__typename === 'YardSpotObject') return 'yard';
    if ('facilityZoneUuid' in locationObject || locationObject.__typename === 'FacilityZoneObject') return 'zone';
    if ('name' in locationObject && !('uuid' in locationObject)) return 'external';
    return 'unknown';
  };

  // Check if task was completed/assigned to driver vs user
  const getCompletedByType = (task: Task): string => {
    // First check completion fields
    if (task.completed_by_driver_id) return 'driver';
    if (task.completed_by_user_id) return 'user';
    
    // Then check assignment/modification fields
    if (task.driver && !task.completed_by_user_id) return 'driver';
    if (task.modified_by_user && !task.completed_by_driver_id) return 'user';
    
    // Fallback to ID fields if objects not available
    if (task.driver_id && !task.completed_by_user_id) return 'driver';
    if (task.modified_by_user_id && !task.completed_by_driver_id) return 'user';
    
    return 'unknown';
  };

  // Get current date range from selected date (single day)
  const getCurrentDateRange = () => {
    return {
      from: selectedDate,
      to: selectedDate
    };
  };

  // Format date for display
  const formatDateForDisplay = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Filter tasks - hide completed and cancelled tasks unless showing archived, plus custom filters
  const getFilteredTasks = () => {
    let filtered = tasks;
    
    // Archive filter
    if (!showArchived) {
      filtered = filtered.filter(task => {
        const status = task.task_status?.status?.toLowerCase();
        return status !== 'completed' && status !== 'cancelled';
      });
    }
    
    // From location filter
    if (fromLocationFilter) {
      filtered = filtered.filter(task => {
        const fromLocation = getLocationName(task.from_object)?.toLowerCase() || '';
        return fromLocation.includes(fromLocationFilter.toLowerCase());
      });
    }
    
    // To location filter
    if (toLocationFilter) {
      filtered = filtered.filter(task => {
        const toLocation = getLocationName(task.to_object)?.toLowerCase() || '';
        return toLocation.includes(toLocationFilter.toLowerCase());
      });
    }
    
    // Location type filter
    if (locationTypeFilter) {
      filtered = filtered.filter(task => {
        const fromType = getLocationType(task.from_object);
        const toType = getLocationType(task.to_object);
        return fromType === locationTypeFilter || toType === locationTypeFilter;
      });
    }
    
    // Completed by filter
    if (completedByFilter) {
      filtered = filtered.filter(task => {
        return getCompletedByType(task) === completedByFilter;
      });
    }
    
    // Note: Date filtering is now handled by the GraphQL query, not client-side
    
    // Task type filter
    if (taskTypeFilter) {
      filtered = filtered.filter(task => {
        return task.task_type?.type === taskTypeFilter;
      });
    }
    
    // Task status filter
    if (taskStatusFilter) {
      filtered = filtered.filter(task => {
        return task.task_status?.status === taskStatusFilter;
      });
    }
    
    // Movement type filter
    if (movementTypeFilter) {
      filtered = filtered.filter(task => {
        // Check for movement_type in the main task data
        return task.data?.movement_type === movementTypeFilter;
      });
    }

    // Trailer number filter
    if (trailerNumberFilter) {
      filtered = filtered.filter(task => {
        const trailerNumber = task.trailer?.trailer_number?.toLowerCase() || '';
        return trailerNumber.includes(trailerNumberFilter.toLowerCase());
      });
    }

    // SCAC filter
    if (scacFilter) {
      filtered = filtered.filter(task => {
        const scac = task.trailer?.carrier?.name?.toLowerCase() || '';
        return scac.includes(scacFilter.toLowerCase());
      });
    }

    return filtered;
  };

  // Reset all filters
  const handleResetFilters = () => {
    setFromLocationFilter('');
    setToLocationFilter('');
    setLocationTypeFilter('');
    setCompletedByFilter('');
    setTaskTypeFilter('');
    setTaskStatusFilter('');
    setMovementTypeFilter('');
    setTrailerNumberFilter('');
    setScacFilter('');
    // Note: Date is not reset as it's a required filter
  };

  // Get unique values for filter dropdowns
  const getUniqueTaskTypes = () => {
    const types = new Set(tasks.map(task => task.task_type?.type).filter(Boolean));
    return Array.from(types).sort();
  };

  const getUniqueTaskStatuses = () => {
    const statuses = new Set(tasks.map(task => task.task_status?.status).filter(Boolean));
    return Array.from(statuses).sort();
  };

  // Check if any filters are active (date is not considered a "filter" since it's required)
  const hasActiveFilters = fromLocationFilter || toLocationFilter || locationTypeFilter ||
    completedByFilter || taskTypeFilter || taskStatusFilter || movementTypeFilter ||
    trailerNumberFilter || scacFilter;

  // Helper function to get location name consistently
  const getLocationName = (locationObject: any): string => {
    if (!locationObject) return 'Unknown';
    if ('name' in locationObject && locationObject.name) return String(locationObject.name);
    if ('number' in locationObject && locationObject.number) return String(locationObject.number);
    return 'Unknown';
  };

  // Initialize checklist state for workflow tasks after data is loaded
  const initializeWorkflowTasksState = (loadedTasks: Task[]) => {
    // Clean up state for tasks that are no longer present
    const currentTaskUuids = new Set(loadedTasks.map(task => task.uuid));
    setChecklistCompletionState(prev => {
      const newState: Record<string, boolean[]> = {};
      Object.keys(prev).forEach(taskUuid => {
        if (currentTaskUuids.has(taskUuid)) {
          newState[taskUuid] = prev[taskUuid];
        }
      });
      return newState;
    });

    // Initialize state for new workflow tasks with checklists
    loadedTasks.forEach(task => {
      if (isWorkflowWithChecklist(task) && !checklistCompletionState[task.uuid]) {
        initializeChecklistState(task.uuid, task.data.checklist.length);
      }
    });
  };

  // Fetch facilities using GraphQL
  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // GraphQL query for facilities
        const facilitiesQuery = `
          query {
            facilities {
              id
              name
            }
          }
        `;

        // Get auth token from cookie
        const authToken = getAuthToken();

        // Fetch facilities using GraphQL
        const response = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: facilitiesQuery
          })
        });

        const result = await response.json();
        
        if (result.errors) {
          throw new Error(result.errors[0].message);
        }

        setFacilities(result.data.facilities);
        
        // Validate localStorage facility still exists, otherwise set default
        const storedFacility = loadFacilityFromStorage();
        if (storedFacility) {
          const facilityExists = result.data.facilities.find((f: any) => f.id === storedFacility.id);
          if (!facilityExists) {
            // Stored facility no longer exists, set default
            if (result.data.facilities.length > 0) {
              setSelectedFacility(result.data.facilities[0]);
            }
          }
          // If facility exists, keep the localStorage value (already set in useState initializer)
        } else {
          // No stored facility, set default
          if (result.data.facilities.length > 0) {
            setSelectedFacility(result.data.facilities[0]);
          }
        }
      } catch (err) {
        console.error('Failed to fetch facilities:', err);
        setError('Failed to fetch facilities');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchFacilities();
  }, []);

  // Fetch trailers in facility (updated to include current location details)
  const fetchTrailersInFacility = async () => {
    if (!selectedFacility) return;
    
    try {
      setIsLoadingTrailers(true);
      
      // Get auth token from cookie
      const authToken = getAuthToken();

      // GraphQL query for trailers in facility
      const trailersQuery = `
        query GetTrailersInFacility($facilityId: ID!) {
          trailers_in_facility(facility_id: $facilityId) {
            uuid
            trailer_number
            carrier {
              name
            }
            current_location {
              ... on YardSpotObject {
                __typename
                uuid
                name
              }
              ... on FacilityZoneObject {
                __typename
                uuid
                name
              }
              ... on DockObject {
                __typename
                uuid
                name
              }
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: trailersQuery,
          variables: {
            facilityId: selectedFacility?.id?.toString() || "1"
          }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      setTrailersInFacility(result.data.trailers_in_facility || []);
    } catch (err) {
      console.error('Failed to fetch trailers:', err);
      setError('Failed to fetch trailers in facility');
    } finally {
      setIsLoadingTrailers(false);
    }
  };

  // Fetch available locations (dock doors and yard spots)
  const fetchAvailableLocations = async () => {
    if (!selectedFacility) return;
    
    try {
      setIsLoadingLocations(true);
      
      // Get auth token from cookie
      const authToken = getAuthToken();

      // GraphQL query for locations
      const locationsQuery = `
        query GetAvailableLocations($facilityId: ID!) {
          warehouse(facilityId: $facilityId) {
            facilityId
            warehouseZones {
              zoneUuid
              zoneName
              docks {
                dockUuid
                dockName
                dockStatus
                trailer_name
                carrier_name
                configuration
              }
            }
          }
          yard(facility_id: $facilityId) {
            facility_zones {
              facility_zone_uuid
              facility_zone_name
              yard_spots {
                yard_spot_uuid
                yard_spot_name
                yard_spot_status
                trailer_name
                carrier_name
              }
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: locationsQuery,
          variables: {
            facilityId: selectedFacility?.id?.toString() || "1"
          }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Process dock doors
      const dockDoors: DockDoor[] = [];
      if (result.data?.warehouse?.warehouseZones) {
        result.data.warehouse.warehouseZones.forEach((zone: any) => {
          if (zone.docks) {
            zone.docks.forEach((dock: any) => {
              if (isDockUnavailableForMovement(dock.configuration)) {
                return;
              }

              dockDoors.push({
                dockUuid: dock.dockUuid,
                dockName: dock.dockName,
                zoneUuid: zone.zoneUuid,
                dockStatus: dock.dockStatus,
                trailer_name: dock.trailer_name,
                carrier_name: dock.carrier_name
              });
            });
          }
        });
      }

      // Process yard spots
      const yardSpots: YardSpot[] = [];
      if (result.data?.yard?.facility_zones) {
        result.data.yard.facility_zones.forEach((zone: any) => {
          if (zone.yard_spots) {
            zone.yard_spots.forEach((spot: any) => {
              yardSpots.push({
                yard_spot_uuid: spot.yard_spot_uuid,
                yard_spot_name: spot.yard_spot_name,
                facility_zone_uuid: zone.facility_zone_uuid,
                facility_zone_name: zone.facility_zone_name,
                yard_spot_status: spot.yard_spot_status,
                trailer_name: spot.trailer_name,
                carrier_name: spot.carrier_name
              });
            });
          }
        });
      }

      setAvailableDockDoors(dockDoors);
      setAvailableYardSpots(yardSpots);
    } catch (err) {
      console.error('Failed to fetch locations:', err);
      setError('Failed to fetch available locations');
    } finally {
      setIsLoadingLocations(false);
    }
  };

  // Fetch tasks using GraphQL
  const fetchTasksData = async () => {
    if (!selectedFacility) return;
    
    try {
      setIsLoadingData(true);
      setError(null);

      // Get auth token from cookie
      const authToken = getAuthToken();

      // GraphQL query for tasks by facility
      const tasksQuery = `
        query GetTasksByFacility($facilityId: ID!, $date_start: String!, $date_end: String!) {
          tasks_report_by_facility(facility_id: $facilityId, date_start: $date_start, date_end: $date_end) {
            tasks {
              uuid
              data
              due_at
              completed_at
              user_id
              facility_id
              driver_id
              completed_by_user_id
              completed_by_driver_id
              modified_by_user_id
              metadata
              created_at
              updated_at
              driver {
                firstName
                lastName
              }
              modified_by_user {
                username
              }
              
              # Related entities
              task_template {
                uuid
                name
                task_template_json
                account_id
              }
              
              task_type {
                uuid
                type
                class
              }
              
              task_status {
                uuid
                status
              }
              from_location
              to_location
              from_object {
                ... on DockObject {
                  uuid
                  name
                }
                ... on YardSpotObject {
                  uuid
                  name
                  facilityZoneUuid
                }
                ... on ExternalLocationObject {
                  name
                }

                ... on FacilityZoneObject {
                  uuid
                  name
                }
              }
             to_object {
                ... on YardSpotObject {
                    uuid
                    name
                }
                ... on DockObject {
                    uuid
                    name
                }
                ... on WarehouseZoneObject {
                    uuid
                    name
                }
                ... on ExternalLocationObject {
                    name
                }
                ... on FacilityZoneObject {
                  uuid
                  name
                }
                }
              trailer {
                uuid
                trailer_number
                carrier {
                    name
                }
              }

              # ADD THIS FIELD FOR SAFETY CHECK TASKS
              related_movement_task {
                uuid
                from_location
                to_location
                data
                from_object {
                  ... on DockObject {
                    uuid
                    name
                  }
                  ... on YardSpotObject {
                    uuid
                    name
                    facilityZoneUuid
                  }
                  ... on ExternalLocationObject {
                    name
                  }
                  ... on FacilityZoneObject {
                    uuid
                    name
                  }
                }
                to_object {
                  ... on YardSpotObject {
                    uuid
                    name
                  }
                  ... on DockObject {
                    uuid
                    name
                  }
                  ... on WarehouseZoneObject {
                    uuid
                    name
                  }
                  ... on ExternalLocationObject {
                    name
                  }
                  ... on FacilityZoneObject {
                    uuid
                    name
                  }
                }
              }
            }
          }
        }
      `;

      // Execute the GraphQL query
      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: tasksQuery,
          variables: {
            facilityId: selectedFacility?.id?.toString() || "1",
            date_start: getCurrentDateRange().from,
            date_end: getCurrentDateRange().to
          }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      setTasks(result.data.tasks_report_by_facility.tasks || []);

      // Initialize checklist state for workflow tasks
      initializeWorkflowTasksState(result.data.tasks_report_by_facility.tasks || []);

    } catch (err) {
      console.error('Failed to fetch tasks:', err);
      setError('Failed to fetch tasks data');
    } finally {
      setIsLoadingData(false);
    }
  };

  // Fetch data when facility or date changes
  useEffect(() => {
    if (selectedFacility) {
      fetchTasksData();
    }
  }, [selectedFacility, selectedDate]);
  
  // Handle facility change
  const handleFacilityChange = (event: any) => {
    const facilityId = event.target.value;
    const facility = facilities.find(f => f.id === facilityId);
    if (facility) {
      setSelectedFacility(facility);
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    if (selectedFacility) {
      fetchTasksData();
    }
  };



  // Filter tasks - no sorting, use data as returned from API
  const filteredTasks = getFilteredTasks();

  // Get status color
  const getStatusColor = (status: string | undefined): "default" | "success" | "primary" | "error" | "secondary" | "info" | "warning" | undefined => {
    if (!status || typeof status !== 'string') return 'default';
    
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'in_progress':
      case 'active':
        return 'primary';
      case 'pending':
        return 'warning';
      case 'failed':
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  // Format date - normalize UTC timestamps and display in user's local timezone
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    // Server sends UTC but may omit timezone signifier - ensure it's treated as UTC
    let normalized = dateString;
    if (!normalized.endsWith('Z') && !normalized.includes('+') && !/[-+]\d{2}:\d{2}$/.test(normalized)) {
      normalized += 'Z';
    }
    return new Date(normalized).toLocaleString();
  };

  // Get priority value (default to 'normal' if null/undefined)
  const getTaskPriority = (task: Task): string => {
    const priority = task.data?.priority;
    if (!priority || typeof priority !== 'string') return 'normal';
    return priority;
  };

  // Get priority color for badge
  const getPriorityColor = (priority: string): "default" | "success" | "primary" | "error" | "secondary" | "info" | "warning" | undefined => {
    if (!priority || typeof priority !== 'string') return 'default';
    switch (priority.toLowerCase()) {
      case 'high':
        return 'error';
      case 'low':
        return 'info';
      case 'normal':
      default:
        return 'default';
    }
  };

  // Handle priority change (plus/minus buttons)
  const handlePriorityChange = async (taskUuid: string, direction: 'up' | 'down') => {
    const task = tasks.find(t => t.uuid === taskUuid);
    if (!task) return;

    // Find all tasks in the same group
    const taskGroupUuid = task.data?.task_group_uuid;
    const relatedTasks = taskGroupUuid 
      ? tasks.filter(t => t.data?.task_group_uuid === taskGroupUuid)
      : [task];

    // Add all related tasks to processing state
    setProcessingTasks(prev => {
      const newSet = new Set(prev);
      relatedTasks.forEach(t => newSet.add(t.uuid));
      return newSet;
    });
    
    try {
      const currentPriority = getTaskPriority(task);
      let newPriority: string;

      if (direction === 'up') {
        // low -> normal -> high -> high (cycle stops at high)
        if (currentPriority === 'low') newPriority = 'normal';
        else if (currentPriority === 'normal') newPriority = 'high';
        else newPriority = 'high'; // already high, stay high
      } else {
        // high -> normal -> low -> low (cycle stops at low)
        if (currentPriority === 'high') newPriority = 'normal';
        else if (currentPriority === 'normal') newPriority = 'low';
        else newPriority = 'low'; // already low, stay low
      }

      // Don't make API call if priority wouldn't change
      if (newPriority === currentPriority) {
        return;
      }

      // Get auth token from cookie
      const authToken = getAuthToken();

      const updatePriorityMutation = `
        mutation UpdateTaskPriority($uuid: String!, $priority: String!) {
          update_task_priority(uuid: $uuid, priority: $priority) {
            uuid
            data
          }
        }
      `;

      // Update priority for all tasks in the group
      const updatePromises = relatedTasks.map(relatedTask => 
        fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: updatePriorityMutation,
            variables: {
              uuid: relatedTask.uuid,
              priority: newPriority
            }
          })
        })
      );

      const results = await Promise.all(updatePromises);
      
      // Check for errors in any of the updates
      for (const response of results) {
        const result = await response.json();
        if (result.errors) {
          throw new Error(result.errors[0].message);
        }
      }

      // Refresh tasks data to reflect the change
      await fetchTasksData();

    } catch (error) {
      console.error('Failed to update task priority:', error);
      setError('Failed to update task priority');
    } finally {
      setProcessingTasks(prev => {
        const newSet = new Set(prev);
        relatedTasks.forEach(t => newSet.delete(t.uuid));
        return newSet;
      });
    }
  };

  // Handle view task details
  const handleViewTask = (taskUuid: string) => {
    navigate(`/tasks/${taskUuid}`);
  };

  // Handle toggle archived
  const handleToggleArchived = (checked: boolean) => {
    if (isTogglingArchived) return; // Prevent stacking changes
    
    setIsTogglingArchived(true);
    setShowArchived(checked);
    
    // Wait for rendering to complete (adjust time based on performance)
    setTimeout(() => {
      setIsTogglingArchived(false);
    }, 1000);
  };

  // Handle cancel task
  const handleCancelTask = async (taskUuid: string) => {
    // Show confirmation dialog
    const confirmed = window.confirm('Are you sure you want to cancel this task?');
    if (!confirmed) {
      return;
    }
    
    setProcessingTasks(prev => new Set(prev).add(taskUuid));
    
    try {
      // Get auth token from cookie
      const authToken = getAuthToken();

      const cancelTaskMutation = `
        mutation CancelTask($taskUuid: String!) {
          cancel_task(uuid: $taskUuid) {
            uuid
            task_status {
              status
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: cancelTaskMutation,
          variables: { taskUuid }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Refresh tasks data
      await fetchTasksData();
      
    } catch (error) {
      console.error('Failed to cancel task:', error);
      setError('Failed to cancel task');
    } finally {
      setProcessingTasks(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskUuid);
        return newSet;
      });
    }
  };

  // Handle unassign task
  const handleUnassignTask = async (taskUuid: string) => {
    setProcessingTasks(prev => new Set(prev).add(taskUuid));
    
    try {
      // Get auth token from cookie
      const authToken = getAuthToken();

      const unassignTaskMutation = `
        mutation UnassignTask($uuid: String!) {
          unassign_task(uuid: $uuid) {
            uuid
            user_id
            driver_id
            task_status {
              status
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: unassignTaskMutation,
          variables: { uuid: taskUuid }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Refresh tasks data
      await fetchTasksData();
      
    } catch (error) {
      console.error('Failed to unassign task:', error);
      setError('Failed to unassign task');
    } finally {
      setProcessingTasks(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskUuid);
        return newSet;
      });
    }
  };

  // Check if task is assigned and can be unassigned (not completed/cancelled/unassigned)
  const isTaskAssigned = (task: Task): boolean => {
    // Don't show unassign for completed, cancelled, or unassigned tasks
    const status = task.task_status?.status?.toLowerCase();
    if (status === 'completed' || status === 'cancelled' || status === 'unassigned') {
      return false;
    }
    
    // Check if task is currently assigned to someone
    return !!(task.driver_id || task.user_id);
  };

  // Handle clear lock




  // Handle create task button click
  const handleCreateTaskClick = () => {
    setIsCreateTaskModalOpen(true);
    setSelectedTaskType('');
    setSelectedTrailer('');
    setSelectedToLocationType('');
    setSelectedToLocation('');
    setAutoComplete(false);
  };

  // Handle task type change
  const handleTaskTypeChange = (event: any) => {
    const taskType = event.target.value;
    setSelectedTaskType(taskType);
    setSelectedTrailer('');
    setSelectedToLocationType('');
    setSelectedToLocation('');
    
    // Fetch trailers when task type is selected
    if (taskType && selectedFacility) {
      fetchTrailersInFacility();
      
      // If it's a MOVE_TRAILER task, also fetch available locations
      if (taskType === 'MOVE_TRAILER') {
        fetchAvailableLocations();
      }
    }
  };

  // Handle to location type change
  const handleToLocationTypeChange = (event: any) => {
    setSelectedToLocationType(event.target.value);
    setSelectedToLocation('');
  };

  // Handle create task
  const handleCreateTask = async () => {
    if (!selectedTaskType || !selectedTrailer || !selectedFacility) return;
    
    // For MOVE_TRAILER tasks, require to location selection
    if (selectedTaskType === 'MOVE_TRAILER' && (!selectedToLocationType || !selectedToLocation)) {
      setError('Please select a destination location for the trailer move');
      return;
    }
    
    try {
      setIsCreatingTask(true);
      
      // Get auth token from cookie
      const authToken = getAuthToken();

      // Use create_movement_task for both MOVE_TRAILER and SAFETY_CHECK tasks
      const createMovementTaskMutation = `
        mutation CreateMovementTask(
          $trailer_uuid: String!,
          $movement_type: String!,
          $facility_id: ID!,
          $warehouse_zone_uuid: String,
          $dock_uuid: String,
          $facility_zone_uuid: String,
          $yard_spot_uuid: String,
          $auto_complete: Boolean,
          $force: Boolean
        ) {
          create_movement_task(input: {
            trailer_uuid: $trailer_uuid
            movement_type: $movement_type
            facility_id: $facility_id
            warehouse_zone_uuid: $warehouse_zone_uuid
            dock_uuid: $dock_uuid
            facility_zone_uuid: $facility_zone_uuid
            yard_spot_uuid: $yard_spot_uuid
            auto_complete: $auto_complete
            force: $force
          }) {
            uuid
            data
            task_type {
              uuid
              type
            }
            task_status {
              uuid
              status
            }
            from_location
            to_location
            trailer {
              uuid
              trailer_number
            }
          }
        }
      `;

      // Determine movement type based on task type and destination
      let movementType = '';
      const variables: any = {
        trailer_uuid: selectedTrailer,
        facility_id: selectedFacility.id.toString(),
        auto_complete: autoComplete,
        force: false
      };

      if (selectedTaskType === 'MOVE_TRAILER') {
        // Handle MOVE_TRAILER task - determine movement type based on destination
        if (selectedToLocationType === 'dock') {
          const selectedDock = availableDockDoors.find(d => d.dockUuid === selectedToLocation);
          if (selectedDock) {
            movementType = 'yard_to_warehouse';
            variables.dock_uuid = selectedDock.dockUuid;
            variables.warehouse_zone_uuid = selectedDock.zoneUuid;
          }
        } else if (selectedToLocationType === 'yard') {
          const selectedSpot = availableYardSpots.find(s => s.yard_spot_uuid === selectedToLocation);
          if (selectedSpot) {
            // Could be warehouse_to_yard or yard_to_yard depending on current location
            // For now, we'll use warehouse_to_yard as default, the backend can determine the actual type
            movementType = 'warehouse_to_yard';
            variables.yard_spot_uuid = selectedSpot.yard_spot_uuid;
            variables.facility_zone_uuid = selectedSpot.facility_zone_uuid;
          }
        }
      } else if (selectedTaskType === 'SAFETY_CHECK') {
        // Handle SAFETY_CHECK task - use inspection movement type
        movementType = 'inspection';
      }

      variables.movement_type = movementType;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: createMovementTaskMutation,
          variables
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      if (!result.data?.create_movement_task?.uuid) {
        throw new Error('Failed to create movement task');
      }

      console.log('Movement task created successfully:', result.data.create_movement_task);

      // Close modal and refresh tasks
      setIsCreateTaskModalOpen(false);
      await fetchTasksData();
      
    } catch (error) {
      console.error('Failed to create task:', error);
      setError('Failed to create task');
    } finally {
      setIsCreatingTask(false);
    }
  };

  // Handle close create task modal
  const handleCloseCreateTaskModal = () => {
    setIsCreateTaskModalOpen(false);
    setSelectedTaskType('');
    setSelectedTrailer('');
    setSelectedToLocationType('');
    setSelectedToLocation('');
    setTrailersInFacility([]);
    setAvailableDockDoors([]);
    setAvailableYardSpots([]);
    setAutoComplete(false);
  };

  // Get location display name
  const getLocationDisplayName = (location: DockDoor | YardSpot, type: string) => {
    if (type === 'dock') {
      const dock = location as DockDoor;
      return `${dock.dockName} ${dock.trailer_name ? '(Occupied)' : '(Available)'}`;
    } else {
      const spot = location as YardSpot;
      return `${spot.yard_spot_name} ${spot.trailer_name ? '(Occupied)' : '(Available)'}`;
    }
  };

  // Loading state for initial facilities fetch
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading facilities...</Typography>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <Typography variant="h6" color="error" sx={{ mb: 2 }}>{error}</Typography>
        <IconButton onClick={handleRefresh}>
          <Refresh />
        </IconButton>
      </div>
    );
  }

  return (
    <div className="p-5">
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
          Tasks
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Facility Selector */}
          <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Facility</InputLabel>
            <Select
              value={selectedFacility?.id || ''}
              onChange={handleFacilityChange}
              label="Facility"
            >
              {facilities.map(facility => (
                <MenuItem key={facility.id} value={facility.id}>
                  {facility.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Date Selector */}
          <TextField
            type="date"
            label="Date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            size="small"
            sx={{ minWidth: 180 }}
            InputLabelProps={{ shrink: true }}
          />

          {/* Create Task Button */}
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={handleCreateTaskClick}
            disabled={!selectedFacility}
          >
            Create Task
          </Button>

          {/* Show Archived Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={showArchived}
                onChange={(e) => handleToggleArchived(e.target.checked)}
                disabled={isTogglingArchived}
                color="primary"
              />
            }
            label="Show Archived Tasks"
            disabled={isTogglingArchived}
          />

          {/* Refresh Button */}
          <IconButton 
            onClick={handleRefresh} 
            disabled={isLoadingData}
            color="primary"
          >
            {isLoadingData ? <CircularProgress size={24} /> : <Refresh />}
          </IconButton>

          {/* Task Count */}
          <Chip 
            label={`${filteredTasks.length} task${filteredTasks.length !== 1 ? 's' : ''} on ${formatDateForDisplay(selectedDate)}${showArchived ? ' (including archived)' : ''}`} 
            color="primary" 
            size="small" 
          />

          {/* Filter indicator */}
          {hasActiveFilters && (
            <Chip
              label="Filters Active"
              color="warning"
              size="small"
              icon={<FilterList />}
            />
          )}
          </Box>

          {/* Spotter/DC Task History Export Button */}
          <Button
            variant="contained"
            color="primary"
            onClick={() => setShowTaskHistoryExportModal(true)}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Spotter/DC Task History Export
          </Button>
        </Box>

        {/* Filters Section */}
        <Accordion sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterList />
              <Typography variant="h6">Filters</Typography>
              {hasActiveFilters && (
                <Button
                  size="small"
                  startIcon={<Clear />}
                  onClick={handleResetFilters}
                  sx={{ ml: 2 }}
                >
                  Clear All
                </Button>
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {/* Row 1: Location Filters */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="From Location"
                  value={fromLocationFilter}
                  onChange={(e) => setFromLocationFilter(e.target.value)}
                  placeholder="Search from location..."
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="To Location"
                  value={toLocationFilter}
                  onChange={(e) => setToLocationFilter(e.target.value)}
                  placeholder="Search to location..."
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Location Type</InputLabel>
                  <Select
                    value={locationTypeFilter}
                    onChange={(e) => setLocationTypeFilter(e.target.value)}
                    label="Location Type"
                  >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value="dock">Dock</MenuItem>
                    <MenuItem value="yard">Yard</MenuItem>
                    <MenuItem value="zone">Zone</MenuItem>
                    <MenuItem value="external">External</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Row 2: Task and Completion Filters */}
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Task Type</InputLabel>
                  <Select
                    value={taskTypeFilter}
                    onChange={(e) => setTaskTypeFilter(e.target.value)}
                    label="Task Type"
                  >
                    <MenuItem value="">All Types</MenuItem>
                    {getUniqueTaskTypes().map(type => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Task Status</InputLabel>
                  <Select
                    value={taskStatusFilter}
                    onChange={(e) => setTaskStatusFilter(e.target.value)}
                    label="Task Status"
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    {getUniqueTaskStatuses().map(status => (
                      <MenuItem key={status} value={status}>{status}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Assigned To</InputLabel>
                  <Select
                    value={completedByFilter}
                    onChange={(e) => setCompletedByFilter(e.target.value)}
                    label="Assigned To"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="driver">Driver</MenuItem>
                    <MenuItem value="user">User</MenuItem>
                    <MenuItem value="unknown">Unknown</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Movement Type</InputLabel>
                  <Select
                    value={movementTypeFilter}
                    onChange={(e) => setMovementTypeFilter(e.target.value)}
                    label="Movement Type"
                  >
                    <MenuItem value="">All Movements</MenuItem>
                    <MenuItem value="leaving_facility">Exited Trailers</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Row 3: Trailer and SCAC Filters */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Trailer #"
                  value={trailerNumberFilter}
                  onChange={(e) => setTrailerNumberFilter(e.target.value)}
                  placeholder="Search trailer number..."
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="SCAC"
                  value={scacFilter}
                  onChange={(e) => setScacFilter(e.target.value)}
                  placeholder="Search SCAC..."
                />
              </Grid>

            </Grid>
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>From → To</TableCell>
                <TableCell>Assigned To</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>Completed</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoadingData ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredTasks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography variant="subtitle1" color="text.secondary">
                      {hasActiveFilters ? 'No tasks match the current filters' : 'No tasks found'}
                    </Typography>
                    {hasActiveFilters && (
                      <Button
                        size="small"
                        onClick={handleResetFilters}
                        sx={{ mt: 1 }}
                      >
                        Clear Filters
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                filteredTasks.map(task => (
                  <TableRow key={task.uuid} hover>
                    <TableCell>
                      <Chip 
                        label={task.task_type?.type || 'Unknown'} 
                        color="info"
                        size="small"
                      />
                      {task.task_type?.type === 'MOVE_TRAILER' && task.from_object && (
                        <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                          {('name' in task.from_object ? task.from_object.name : 'number' in task.from_object ? task.from_object.number : 'Unknown')} → {task.to_object ? ('name' in task.to_object ? task.to_object.name : 'number' in task.to_object ? task.to_object.number : 'Unknown') : 'TBD'}
                        </Typography>
                      )}
                      {task.task_type?.type === 'SAFETY_CHECK' && (
                        <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                          {task.related_movement_task && task.related_movement_task.from_object ? (
                            <>
                              Safety Check: {getLocationName(task.related_movement_task.from_object)} → {task.related_movement_task.to_object ? getLocationName(task.related_movement_task.to_object) : 'TBD'}
                            </>
                          ) : (
                            <>
                              {task.data?.dock_uuid ? 'Dock Safety Check' : task.data?.yard_spot_uuid ? 'Yard Safety Check' : 'Safety Check'}
                            </>
                          )}
                        </Typography>
                      )}
                      {task.trailer && (
                        <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>
                          Trailer: {task.trailer.trailer_number} | SCAC: {task.trailer.carrier?.name || 'N/A'}
                        </Typography>
                      )}
                      {task.task_type?.type === 'WORKFLOW' && task.data?.workflow_type && (
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" display="block" sx={{ 
                            color: 'text.secondary',
                            fontSize: '0.75rem'
                          }}>
                            {formatWorkflowType(task.data.workflow_type)}
                          </Typography>
                          {isWorkflowWithChecklist(task) && (
                            <Typography variant="caption" display="block" sx={{ 
                              color: canCompleteWorkflowTask(task) ? 'success.main' : 'warning.main',
                              fontSize: '0.75rem',
                              fontWeight: 'bold'
                            }}>
                              Checklist: {checklistCompletionState[task.uuid] ? 
                                `${checklistCompletionState[task.uuid].filter(Boolean).length}/${task.data.checklist.length}` : 
                                `0/${task.data.checklist.length}`} completed
                            </Typography>
                          )}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={task.task_status?.status || 'Unknown'} 
                        color={getStatusColor(task.task_status?.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip 
                          label={getTaskPriority(task)} 
                          color={getPriorityColor(getTaskPriority(task))}
                          size="medium"
                          sx={{ 
                            fontSize: '0.85rem',
                            flex: 1,
                            minWidth: 0
                          }}
                        />
                        {/* Priority Controls */}
                        <Tooltip title="Increase Priority" arrow>
                          <IconButton
                            size="medium"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePriorityChange(task.uuid, 'up');
                            }}
                            disabled={processingTasks.has(task.uuid) || getTaskPriority(task) === 'high'}
                            sx={{ 
                              padding: '6px',
                              bgcolor: getTaskPriority(task) === 'high' ? 'grey.400' : 'success.light',
                              color: 'white',
                              '&:hover': { bgcolor: getTaskPriority(task) === 'high' ? 'grey.400' : 'success.main' }
                            }}
                          >
                            <Add sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Decrease Priority" arrow>
                          <IconButton
                            size="medium"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePriorityChange(task.uuid, 'down');
                            }}
                            disabled={processingTasks.has(task.uuid) || getTaskPriority(task) === 'low'}
                            sx={{ 
                              padding: '6px',
                              bgcolor: getTaskPriority(task) === 'low' ? 'grey.400' : 'warning.light',
                              color: 'white',
                              '&:hover': { bgcolor: getTaskPriority(task) === 'low' ? 'grey.400' : 'warning.main' }
                            }}
                          >
                            <Remove sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {/* From → To Location Display */}
                      <Box sx={{ minWidth: 150 }}>
                        {task.from_object || task.to_object ? (
                          <Typography variant="body2">
                            <Box component="span" sx={{ fontWeight: 'medium' }}>
                              {getLocationName(task.from_object) || 'Unknown'}
                            </Box>
                            {' → '}
                            <Box component="span" sx={{ fontWeight: 'medium' }}>
                              {getLocationName(task.to_object) || 'TBD'}
                            </Box>
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No locations
                          </Typography>
                        )}
                        {/* Location types as small chips */}
                        <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {task.from_object && (
                            <Chip 
                              label={getLocationType(task.from_object)} 
                              size="small" 
                              variant="outlined"
                              sx={{ fontSize: '0.65rem', height: 16 }}
                            />
                          )}
                          {task.to_object && (
                            <Chip 
                              label={getLocationType(task.to_object)} 
                              size="small" 
                              variant="outlined"
                              sx={{ fontSize: '0.65rem', height: 16 }}
                            />
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {/* Completed By Display */}
                      <Box sx={{ minWidth: 120 }}>
                        {(() => {
                          const completedByType = getCompletedByType(task);
                          let displayName = completedByType === 'driver' ? 'Driver' : completedByType === 'user' ? 'User' : 'Unknown';
                          let tooltipText = '';
                          
                          // Get actual names when available and determine if completed or assigned
                          const isCompleted = !!task.completed_at;
                          
                          if (completedByType === 'driver' && task.driver) {
                            displayName = `${task.driver.firstName} ${task.driver.lastName}`;
                            tooltipText = isCompleted ? 'Task completed by driver' : 'Task assigned to driver';
                          } else if (completedByType === 'user' && task.modified_by_user) {
                            displayName = task.modified_by_user.username;
                            tooltipText = isCompleted ? 'Task completed by user' : 'Task assigned to user';
                          } else if (completedByType === 'driver') {
                            tooltipText = isCompleted ? 'Task completed by driver' : 'Task assigned to driver';
                          } else if (completedByType === 'user') {
                            tooltipText = isCompleted ? 'Task completed by user' : 'Task assigned to user';
                          }
                          
                          const chipElement = (
                            <Chip 
                              label={displayName}
                              color={completedByType === 'driver' ? 'primary' : completedByType === 'user' ? 'secondary' : 'default'}
                              size="small"
                              variant={task.completed_at ? 'filled' : 'outlined'}
                              sx={{ maxWidth: '100%' }}
                            />
                          );
                          
                          return tooltipText ? (
                            <Tooltip title={tooltipText} arrow>
                              {chipElement}
                            </Tooltip>
                          ) : chipElement;
                        })()}
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(task.created_at)}</TableCell>
                    <TableCell>{formatDate(task.due_at)}</TableCell>
                    <TableCell>{formatDate(task.completed_at)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        
                        <Button
                          size="small"
                          startIcon={<Visibility />}
                          onClick={() => handleViewTask(task.uuid)}
                          variant="outlined"
                        >
                          View
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          startIcon={
                            processingTasks.has(task.uuid) ? 
                            <CircularProgress size={12} color="inherit" /> : 
                            <Cancel />
                          }
                          onClick={() => handleCancelTask(task.uuid)}
                          disabled={processingTasks.has(task.uuid) || task.task_status?.status?.toLowerCase() === 'completed' || task.task_status?.status?.toLowerCase() === 'cancelled'}
                        >
                          {processingTasks.has(task.uuid) ? 'Cancelling...' : 'Cancel'}
                        </Button>

                        {isTaskAssigned(task) && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={
                              processingTasks.has(task.uuid) ? 
                              <CircularProgress size={12} color="inherit" /> : 
                              <AssignmentReturn />
                            }
                            onClick={() => handleUnassignTask(task.uuid)}
                            disabled={processingTasks.has(task.uuid)}
                          >
                            {processingTasks.has(task.uuid) ? 'Unassigning...' : 'Unassign'}
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create Task Modal */}
      <Dialog
        open={isCreateTaskModalOpen}
        onClose={handleCloseCreateTaskModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New Task</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            {/* Task Type Selector */}
            <FormControl fullWidth>
              <InputLabel>Task Type</InputLabel>
              <Select
                value={selectedTaskType}
                onChange={handleTaskTypeChange}
                label="Task Type"
              >
                <MenuItem value="MOVE_TRAILER">Move Trailer</MenuItem>
                <MenuItem value="SAFETY_CHECK">Safety Check</MenuItem>
              </Select>
            </FormControl>

            {/* Trailer Selector */}
            {selectedTaskType && (
              <FormControl fullWidth>
                <InputLabel>Select Trailer</InputLabel>
                <Select
                  value={selectedTrailer}
                  onChange={(e) => setSelectedTrailer(e.target.value)}
                  label="Select Trailer"
                  disabled={isLoadingTrailers}
                >
                  {isLoadingTrailers ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Loading trailers...
                    </MenuItem>
                  ) : trailersInFacility.length === 0 ? (
                    <MenuItem disabled>No trailers available</MenuItem>
                  ) : (
                    trailersInFacility.map(trailer => (
                      <MenuItem key={trailer.uuid} value={trailer.uuid}>
                        <Box>
                          <Typography variant="body2">
                            {trailer.trailer_number}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {trailer.carrier?.name || 'No carrier'} | {
                              trailer.current_location 
                                ? `${trailer.current_location.__typename}: ${trailer.current_location.name}`
                                : 'Unknown location'
                            }
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            )}

            {/* Location Selection for MOVE_TRAILER tasks */}
            {selectedTaskType === 'MOVE_TRAILER' && (
              <>
                <FormControl fullWidth>
                  <InputLabel>To Location Type</InputLabel>
                  <Select
                    value={selectedToLocationType}
                    onChange={handleToLocationTypeChange}
                    label="To Location Type"
                  >
                    <MenuItem value="dock">Dock Door</MenuItem>
                    <MenuItem value="yard">Yard Spot</MenuItem>
                  </Select>
                </FormControl>

                {selectedToLocationType && (
                  <FormControl fullWidth>
                    <InputLabel>
                      {selectedToLocationType === 'dock' ? 'Select Dock Door' : 'Select Yard Spot'}
                    </InputLabel>
                    <Select
                      value={selectedToLocation}
                      onChange={(e) => setSelectedToLocation(e.target.value)}
                      label={selectedToLocationType === 'dock' ? 'Select Dock Door' : 'Select Yard Spot'}
                      disabled={isLoadingLocations}
                    >
                      {isLoadingLocations ? (
                        <MenuItem disabled>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Loading locations...
                        </MenuItem>
                      ) : selectedToLocationType === 'dock' ? (
                        availableDockDoors.length === 0 ? (
                          <MenuItem disabled>No dock doors available</MenuItem>
                        ) : (
                          availableDockDoors.map(dock => (
                            <MenuItem key={dock.dockUuid} value={dock.dockUuid}>
                              <Box>
                                <Typography variant="body2">
                                  {getLocationDisplayName(dock, 'dock')}
                                </Typography>
                                {dock.trailer_name && (
                                  <Typography variant="caption" color="text.secondary">
                                    Current trailer: {dock.trailer_name} | {dock.carrier_name || 'Unknown carrier'}
                                  </Typography>
                                )}
                              </Box>
                            </MenuItem>
                          ))
                        )
                      ) : (
                        availableYardSpots.length === 0 ? (
                          <MenuItem disabled>No yard spots available</MenuItem>
                        ) : (
                          availableYardSpots.map(spot => (
                            <MenuItem key={spot.yard_spot_uuid} value={spot.yard_spot_uuid}>
                              <Box>
                                <Typography variant="body2">
                                  {getLocationDisplayName(spot, 'yard')}
                                </Typography>
                                {spot.trailer_name && (
                                  <Typography variant="caption" color="text.secondary">
                                    Current trailer: {spot.trailer_name} | {spot.carrier_name || 'Unknown carrier'}
                                  </Typography>
                                )}
                              </Box>
                            </MenuItem>
                          ))
                        )
                      )}
                    </Select>
                  </FormControl>
                )}

                {/* Auto Complete Checkbox */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={autoComplete}
                      onChange={(e) => setAutoComplete(e.target.checked)}
                    />
                  }
                  label="Auto Complete - The move has already happened, just recording it"
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateTaskModal}>Cancel</Button>
          <Button
            onClick={handleCreateTask}
            variant="contained"
            disabled={
              !selectedTaskType || 
              !selectedTrailer || 
              (selectedTaskType === 'MOVE_TRAILER' && (!selectedToLocationType || !selectedToLocation)) ||
              isCreatingTask
            }
            startIcon={isCreatingTask ? <CircularProgress size={16} /> : null}
          >
            {isCreatingTask ? 'Creating...' : 'Create Task'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Spotter/DC Task History Export Modal */}
      {showTaskHistoryExportModal && (() => {
        try {
          const authToken = getAuthToken() || '';
          if (!authToken) return null;
          const decoded: any = jwtDecode(authToken);
          const publicUserId = decoded?.user_id;
          if (!publicUserId) return null;

          return (
            <TaskHistoryExportModal
              open={showTaskHistoryExportModal}
              onClose={() => setShowTaskHistoryExportModal(false)}
              facilities={facilities}
              userId={publicUserId}
              jwtToken={authToken}
              websocketUrl={REPORTS_WEBSOCKET_URL}
            />
          );
        } catch (error) {
          console.error('Failed to decode JWT for task history export:', error);
          return null;
        }
      })()}
    </div>
  );
};

export default Tasks; 